// Claims and variations list (V0.4.3): approval progress and money. Each row shows its step — SWI review (orange) →
// SWI approved (light green) → Client approved (dark green) → Paid / Complete (faded) — with a 4-part strip, the
// certificate state on claims, work progress + part payments on variations. Summary on top (MoneySummary).
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { write } from '../lib/api';
import { fmtDate, fmtMoney } from '../lib/format';
import { isClosed, itemFields, needsReview, registerFor, statusLabel, TYPE_LOOK, type Item } from '../lib/registers';
import { progressOf, raisedOn, workQ, type Progress } from '../lib/progress';
import { fijiVat, otherBasis, pctText } from '../lib/vat';
import { TypeTag } from '../ui/TypeTag';
import { Button, Card, Empty, PageHeader, errText, useToast } from '../ui/kit';
import { MoneySummary, Meter, Strip, WORK_LABEL, type MoneyFilter } from '../ui/MoneySummary';
import { useSummaryPrefs } from '../ui/RegisterSummary';
import { type Org } from '../ui/item/shared';
import { RecordDrawer } from '../ui/RecordDrawer';

const NONE = '00000000-0000-0000-0000-000000000000';
type Row = Pick<
  Item,
  'id' | 'type' | 'number' | 'ref_no' | 'subtype' | 'title' | 'status' | 'stage' | 'building_ids' | 'buildings_raw' | 'org_id' | 'org_raw' | 'submitted_on' | 'respond_by' | 'amount' | 'data' | 'review' | 'ss_created_at' | 'created_at' | 'updated_at' | 'deleted_at'
> & { swi_approved_at: string | null; client_approved_at: string | null; work_progress: string | null; paid_total: number | null; paid_on: string | null };
type PRow = Row & { p: Progress; alt: number; vat: number };

const QUICK: Record<string, string> = {
  review: 'With SWI to review',
  swi: 'SWI approved',
  client: 'Client approved',
  paid: 'Paid',
  complete: 'Complete',
  hold: 'On hold',
  other: 'Hold · rejected',
  stale: 'Over 90 days',
};
const when = (r: Row) => raisedOn(r);
const numOf = (r: Row) => Number(/(\d+)\s*$/.exec(r.number ?? '')?.[1] ?? -1);
const SORTS: { key: string; label: string; cmp: (a: PRow, b: PRow) => number }[] = [
  { key: 'latest', label: 'Latest first', cmp: (a, b) => when(b).localeCompare(when(a)) || numOf(b) - numOf(a) },
  { key: 'oldest', label: 'Oldest first', cmp: (a, b) => when(a).localeCompare(when(b)) || numOf(a) - numOf(b) },
  { key: 'num_desc', label: 'Number — highest first', cmp: (a, b) => numOf(b) - numOf(a) },
  { key: 'num_asc', label: 'Number — lowest first', cmp: (a, b) => numOf(a) - numOf(b) },
  { key: 'topic_az', label: 'Topic A–Z', cmp: (a, b) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }) },
  { key: 'topic_za', label: 'Topic Z–A', cmp: (a, b) => b.title.localeCompare(a.title, 'en', { sensitivity: 'base' }) },
  { key: 'progress', label: 'Progress — least advanced first', cmp: (a, b) => ORDER.indexOf(a.p.step) - ORDER.indexOf(b.p.step) || b.p.days - a.p.days },
  { key: 'waiting', label: 'Waiting longest first', cmp: (a, b) => b.p.days - a.p.days },
  { key: 'amount', label: 'Amount — highest first', cmp: (a, b) => Number(b.amount ?? 0) - Number(a.amount ?? 0) },
];
const ORDER = ['review', 'hold', 'swi', 'client', 'paid', 'complete', 'rejected'];

export default function MoneyRegister() {
  const { module } = useParams();
  const def = registerFor(module);
  const { siteId, site, can, session } = useApp();
  const nav = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(false);
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? 'all';
  const sort = params.get('sort') ?? 'latest';
  const building = params.get('b') ?? '';
  const review = params.get('review') === '1';
  const quick = params.get('f') ?? '';
  const workF = params.get('work') ?? '';
  const [prefs, setPrefs] = useSummaryPrefs(session?.user.id, module ?? '');
  const setParam = (k: string, v: string) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n, { replace: true });
  };

  const list = useRows<Row>(
    () =>
      supabase
        .from('items')
        .select(
          'id, type, number, ref_no, subtype, title, status, stage, building_ids, buildings_raw, org_id, org_raw, submitted_on, respond_by, amount, data, review, swi_approved_at, client_approved_at, work_progress, paid_total, paid_on, ss_created_at, created_at, updated_at, deleted_at',
        )
        .eq('site_id', siteId ?? NONE)
        .eq('type', def?.type ?? 'none')
        .is('deleted_at', null)
        .limit(5000),
    [siteId, def?.type],
    ['items'],
  );
  const buildings = useRows<{ id: string; name: string }>(
    () => supabase.from('buildings').select('id, name').eq('site_id', siteId ?? NONE).is('deleted_at', null).order('sort').order('name'),
    [siteId],
    ['buildings'],
  );
  const orgs = useRows<Org>(() => supabase.from('organisations').select('id, name, short_code, aliases, kind').is('deleted_at', null).order('name'), [], ['organisations']);
  const bName = useMemo(() => new Map(buildings.rows.map((b) => [b.id, b.name])), [buildings.rows]);
  const oName = useMemo(() => new Map(orgs.rows.map((o) => [o.id, o.short_code && o.name.length > 14 ? o.short_code : o.name])), [orgs.rows]);
  const basis = def?.money ?? 'VEP';
  // the other basis of each amount (claims are VIP → VEP; variations VEP → VIP) at the Fiji VAT rate on the item's date
  const rows: PRow[] = useMemo(
    () =>
      list.rows.map((r) => {
        const vat = fijiVat(raisedOn(r));
        return { ...r, p: progressOf(r), vat, alt: otherBasis(Number(r.amount ?? 0), basis, vat).amount };
      }),
    [list.rows, basis],
  );
  const toReview = useMemo(() => rows.filter(needsReview).length, [rows]);

  const shown = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const cmp = (SORTS.find((x) => x.key === sort) ?? SORTS[0]).cmp;
    return rows
      .filter((r) => {
        if (status === 'open' && isClosed(r.status)) return false;
        if (status !== 'open' && status !== 'all' && r.status !== status) return false;
        if (building === 'none' ? r.building_ids.length > 0 : building && !r.building_ids.includes(building)) return false;
        if (review && !needsReview(r)) return false;
        if (quick === 'stale' && !r.p.stale) return false;
        if (quick === 'other' && r.p.step !== 'hold' && r.p.step !== 'rejected') return false;
        if (quick && quick !== 'stale' && quick !== 'other' && r.p.step !== quick) return false;
        if (workF && (r.p.step !== 'client' || (workF === 'unset' ? r.p.work !== null : r.p.work !== workF))) return false;
        if (!words.length) return true;
        const hay = [r.number, r.ref_no, r.subtype, r.title, ...r.buildings_raw, r.org_raw].join(' ').toLowerCase();
        return words.every((w) => hay.includes(w));
      })
      .sort(cmp);
  }, [rows, q, status, building, sort, review, quick, workF]);

  if (!def) return <Navigate to="/" replace />;
  if (!can(def.module)) return <Navigate to="/" replace />;
  const claim = def.type === 'claim';
  const editable = can(def.module, 4);
  const total = shown.reduce((s, r) => s + Number(r.amount ?? 0), 0);
  const t = new Date().toISOString().slice(0, 10);
  const open = (id: string) => nav(`/r/${def.module}/${id}`);
  const chips: { label: string; clear: () => void }[] = [];
  if (quick) chips.push({ label: QUICK[quick] ?? quick, clear: () => setParam('f', '') });
  if (workF) chips.push({ label: `Work: ${workF === 'unset' ? 'not set' : WORK_LABEL(workF as never)}`, clear: () => setParam('work', '') });
  if (building) chips.push({ label: building === 'none' ? 'No building' : (bName.get(building) ?? 'Building'), clear: () => setParam('b', '') });
  const filter: MoneyFilter = { f: quick, work: workF, b: building };
  const lateN = rows.filter((r) => r.respond_by && r.respond_by < t && ['review', 'hold'].includes(r.p.step)).length;
  const count = (s: string) => rows.filter((r) => r.p.step === s).length;

  const tick = async (r: PRow) => {
    try {
      await write('items', 'update', { id: r.id, values: { status: 'complete', stage: 'complete', _expect_updated_at: r.updated_at }, summary: 'Variation ticked complete' });
      toast({ text: `${r.number ?? 'Variation'} ticked complete`, tone: 'ok' });
      void list.reload();
    } catch (e) {
      toast({ text: errText(e), tone: 'error' });
    }
  };

  return (
    <>
      <div className="type-band" style={{ background: TYPE_LOOK[def.type].color }} />
      <div className="register-page">
        <PageHeader
          title={def.label}
          sub={
            <span className="item-head">
              <TypeTag type={def.type} /> {site?.name} · amounts FJD {def.money}
            </span>
          }
          actions={
            <>
              <Button kind="secondary" onClick={() => setPrefs({ hidden: !prefs.hidden })} aria-pressed={!prefs.hidden}>
                {prefs.hidden ? 'Show summary' : 'Hide summary'}
              </Button>
              {editable ? (
                <Button kind="primary" onClick={() => setAdding(true)}>
                  + New {def.one}
                </Button>
              ) : null}
            </>
          }
        />
        {!list.loading && rows.length ? (
          <MoneySummary
            type={claim ? 'claim' : 'variation'}
            rows={rows}
            buildings={buildings.rows}
            filter={filter}
            setFilter={(k, v) => setParam(k, v)}
            prefs={prefs}
            setPrefs={setPrefs}
            paymentsLink={can('claims') || can('variations')}
          />
        ) : null}
        <div className="toolbar">
          <input type="search" placeholder={claim ? 'Find by number, claim #, building…' : 'Find by number, VPR, building…'} value={q} onChange={(e) => setParam('q', e.target.value)} aria-label="Find" style={{ minWidth: 240 }} />
          <select value={status} onChange={(e) => setParam('status', e.target.value === 'all' ? '' : e.target.value)} aria-label="Status">
            <option value="all">All items</option>
            <option value="open">Open items</option>
            {def.statuses.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s, def.type)}
              </option>
            ))}
          </select>
          <select value={sort} onChange={(e) => setParam('sort', e.target.value === 'latest' ? '' : e.target.value)} aria-label="Sort">
            {SORTS.map((x) => (
              <option key={x.key} value={x.key}>
                {x.label}
              </option>
            ))}
          </select>
          <select value={building} onChange={(e) => setParam('b', e.target.value)} aria-label="Building">
            <option value="">All buildings</option>
            {buildings.rows.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
            <option value="none">No building</option>
          </select>
          {chips.map((c) => (
            <button key={c.label} type="button" className="chip-btn filter on" onClick={c.clear} aria-label={`Clear filter ${c.label}`}>
              {c.label} ✕
            </button>
          ))}
          {toReview || review ? (
            <button type="button" className={`chip-btn review ${review ? 'on' : ''}`} aria-pressed={review} onClick={() => setParam('review', review ? '' : '1')}>
              To be reviewed · {toReview}
            </button>
          ) : null}
          <span className="row-key" aria-label="Row colours">
            <span><i className="k-open" />SWI review</span>
            <span><i className="k-swiok" />SWI approved</span>
            <span><i className="k-client" />Client approved</span>
            <span><i className="k-closed" />{claim ? 'Paid' : 'Complete'}</span>
          </span>
        </div>
        <Card pad={false}>
          {list.error ? <div className="error-box">{list.error}</div> : null}
          {list.loading ? (
            <Empty>Loading…</Empty>
          ) : shown.length === 0 ? (
            <Empty>{rows.length ? 'Nothing matches these filters.' : `No ${def.label.toLowerCase()} yet — import them from Smartsheet (Admin → Import) or add one.`}</Empty>
          ) : (
            <div className="table-wrap">
              <table className={`t reg mreg ${claim ? 'claims' : 'vars'}`}>
                <thead>
                  <tr>
                    <th className="c-num">No.</th>
                    <th>{claim ? 'Claim' : 'Variation'}</th>
                    <th className="c-prog">Progress</th>
                    {!claim ? <th className="c-work">Work</th> : null}
                    <th className="right c-amt">Amount {def.money}</th>
                    <th className="c-date">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const p = r.p;
                    const names = r.building_ids.map((id) => bName.get(id)).filter(Boolean) as string[];
                    const where = names.length ? names.join(', ') : r.buildings_raw.join(', ') || (claim ? 'No building' : '');
                    const who = (r.org_id && oName.get(r.org_id)) || r.org_raw;
                    const sub = [r.subtype && !claim ? r.subtype : null, where, who].filter(Boolean).join(' · ');
                    const late = r.respond_by && r.respond_by < t && (p.step === 'review' || p.step === 'hold');
                    const age = p.step === 'paid' ? `paid ${fmtDate(r.paid_on ?? null)}` : p.step === 'complete' ? 'complete' : p.step === 'rejected' ? 'rejected'
                      : p.stale ? (claim ? `${p.days} days — paid?` : `${p.days} days with client`)
                      : p.step === 'review' ? `${p.days} days with SWI` : p.step === 'swi' ? `${p.days} days with ${claim ? 'finance' : 'client'}` : p.step === 'client' ? (claim ? `${p.days} days to pay` : 'approved') : `on hold ${p.days} days`;
                    const cls = ['click', `m-${p.step}`, p.stale ? 'm-stale' : '', p.step === 'review' && p.days > 14 ? 'm-slow' : ''].filter(Boolean).join(' ');
                    const q4 = workQ(p.work);
                    return (
                      <tr key={r.id} className={cls} onClick={() => open(r.id)}>
                        <td className="c-num">
                          <a className="num" href={`/r/${def.module}/${r.id}`} onClick={(e) => (e.preventDefault(), open(r.id))}>
                            {r.number ?? '—'}
                          </a>
                          {r.ref_no ? <div className="muted small">{r.ref_no}</div> : null}
                          {needsReview(r) ? <span className="review-dot">To review</span> : null}
                        </td>
                        <td>
                          <div className="clip">{r.title}</div>
                          {sub ? <div className="sub-line">{sub}</div> : null}
                          <div className="m-only m-prog">
                            <span className={`pchip s-${p.step}`}>{p.label}</span>
                            {p.cert === 'cert' ? <span className="ptag cert">Cert ✓</span> : p.cert === 'for' ? <span className="ptag cert">For cert</span> : null}
                            {p.stale && claim ? <span className="ptag paidq">Paid?</span> : null}
                            {!claim && p.work ? <span className="sub-line">{WORK_LABEL(p.work)}</span> : null}
                            {editable && !claim && p.step === 'client' && p.work === 'full' ? (
                              <button type="button" className="nudge tick" onClick={(e) => (e.stopPropagation(), void tick(r))}>
                                ✓ tick complete
                              </button>
                            ) : null}
                            <span className="sub-line m-amt">
                              <b>FJD {fmtMoney(r.amount).replace('FJD ', '')}</b> {def.money}
                              {r.amount ? ` · ${fmtMoney(r.alt).replace('FJD ', '')} ${claim ? 'VEP' : 'VIP'}` : ''}
                              {!claim && p.paidPct !== null ? ` · ${p.paidPct} % paid` : ''}
                            </span>
                            <span className="sub-line">
                              {fmtDate(r.submitted_on)} · {age}
                            </span>
                            {late ? <span className="sub-line overdue">respond by {fmtDate(r.respond_by)} · overdue</span> : null}
                          </div>
                        </td>
                        <td className="c-prog">
                          <div className="prog-row">
                            <span className={`pchip s-${p.step}`}>
                              {p.step === 'swi' || p.step === 'client' || p.step === 'paid' || p.step === 'complete' ? '✓ ' : ''}
                              {p.label}
                            </span>
                            {p.cert === 'cert' ? <span className="ptag cert">Cert ✓</span> : p.cert === 'for' ? <span className="ptag cert">For cert</span> : null}
                            {p.stale && claim ? <span className="ptag paidq">Paid?</span> : null}
                          </div>
                          <Strip step={p.step} />
                        </td>
                        {!claim ? (
                          <td className="c-work">
                            {p.step === 'client' || p.step === 'complete' || p.work ? (
                              <>
                                <Meter q={q4} faded={p.step === 'complete'} />
                                <div className="sub-line">{WORK_LABEL(p.work)}</div>
                              </>
                            ) : null}
                            {editable && p.step === 'client' && p.work === 'full' ? (
                              <button type="button" className="nudge tick" onClick={(e) => (e.stopPropagation(), void tick(r))}>
                                ✓ tick complete
                              </button>
                            ) : p.step === 'client' ? (
                              <a className="nudge upd" href={`/r/${def.module}/${r.id}#approval`} onClick={(e) => (e.preventDefault(), e.stopPropagation(), nav(`/r/${def.module}/${r.id}#approval`))}>
                                <span className="pdot o" aria-hidden="true" />
                                update progress
                              </a>
                            ) : null}
                          </td>
                        ) : null}
                        <td className={`right c-amt ${Number(r.amount ?? 0) < 0 ? 'neg' : ''}`}>
                          <b>{fmtMoney(r.amount).replace('FJD ', '')}</b>
                          {r.amount ? (
                            <div className="sub-line alt-basis" title={`VAT ${pctText(r.vat)}`}>
                              {claim ? 'VEP' : 'VIP'} {fmtMoney(r.alt).replace('FJD ', '')}
                            </div>
                          ) : null}
                          {!claim && p.paidPct !== null ? (
                            <>
                              <div className="sub-line">
                                {p.paidPct} % paid · {fmtMoney(r.paid_total).replace('FJD ', '')}
                              </div>
                              <span className="paidbar" aria-hidden="true">
                                <span style={{ width: `${p.paidPct}%` }} />
                              </span>
                            </>
                          ) : null}
                        </td>
                        <td className="c-date">
                          <span className="nowrap">{fmtDate(r.submitted_on)}</span>
                          <div className={`sub-line ${p.stale ? 'stale' : p.step === 'review' && p.days > 14 ? 'soon' : ''}`}>{age}</div>
                          {late ? <div className="sub-line overdue">respond by {fmtDate(r.respond_by)} · overdue</div> : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!list.loading && rows.length ? (
            <div className="count-line">
              Showing {shown.length} of {rows.length} · {count('review')} with SWI · {count('swi')} SWI approved · {count('client')} client approved ·{' '}
              {claim ? `${count('paid')} paid` : `${count('complete')} complete`}
              {lateN ? ` · ${lateN} overdue` : ''} · total {fmtMoney(total)} {def.money} ({fmtMoney(shown.reduce((s, r) => s + r.alt, 0))} {claim ? 'VEP' : 'VIP'})
            </div>
          ) : null}
        </Card>
      </div>

      <RecordDrawer
        open={adding}
        onClose={() => setAdding(false)}
        title={`New ${def.one}`}
        table="items"
        fields={itemFields(def, buildings.rows, orgs.rows)}
        record={null}
        defaults={{ site_id: siteId, type: def.type, status: def.statuses[0], amount_basis: def.money ?? null }}
        canEdit={editable}
        onSaved={(row) => row?.id && nav(`/r/${def.module}/${row.id}`)}
        label={(r) => `${def.one} ${String(r.title ?? '').slice(0, 60)}`}
      />
    </>
  );
}
